import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { queryClient } from '../Core/Query/QueryClient';
import { pokerGraphqlRequest } from '../Utils/PokerGraphqlClient';
import type { ILobbyTournamentState, ITournamentLobbyPageInfo, ITournamentState } from '../Core/State/ILobbyTournamentState';

const TOURNAMENT_SCHEDULE_PAGE_SIZE = 200;
export const TOURNAMENT_SCHEDULE_UI_SEGMENT_SIZE = 20;
const TOURNAMENT_SCHEDULE_GRAPHQL_MAX_WINDOW_DAYS = 62;
const TOURNAMENT_SCHEDULE_GLOBAL_PRESET_WINDOW_DAYS = 370;
const TOURNAMENT_SCHEDULE_REFRESH_MS = 45_000;
const TOURNAMENT_LOBBY_POLL_INTERVAL_MS = 5_000;
const HOME_TOURNAMENT_SUMMARY_LIMIT = 20;
const HOME_TOURNAMENT_SUMMARY_WINDOW_DAYS = 7;
const TOURNAMENT_LOBBY_DEBUG_GAME_ID_STORAGE_KEY = 'hsTournamentLobbyDebugGameId';
const TOURNAMENT_LOBBY_POLLING_REFRESH_EVENT = 'hsTournamentLobbyPollingRefresh';
const UPCOMING_TOURNAMENT_STATUS_FILTER = ['REGISTRATION', 'SCHEDULED', 'STARTING'];

const TOURNAMENT_SCHEDULE_QUERY = `
  query TournamentSchedule(
    $startTimeFrom: Timestamp!
    $startTimeTo: Timestamp!
    $statusIn: [TournamentStatus!]
    $gameType: GameType
    $nameContains: String
    $isSatellite: Boolean
    $isEvent: Boolean
    $registrationEntryModeIn: [TournamentRegistrationEntryMode!]
    $first: Int
    $after: String
  ) {
    tournamentSchedule(
      startTimeFrom: $startTimeFrom
      startTimeTo: $startTimeTo
      statusIn: $statusIn
      gameType: $gameType
      nameContains: $nameContains
      isSatellite: $isSatellite
      isEvent: $isEvent
      registrationEntryModeIn: $registrationEntryModeIn
      first: $first
      after: $after
      sortBy: START_TIME
      sortDirection: ASC
    ) {
      items {
        id
        gameId
        tournamentId
        tournamentTemplateId
        name
        imageName
        gameType
        status
        maxPlayers
        registeredPlayerCount
        buyIn
        entryFee
        prizePool
        guaranteedPrizePool
        startTime
        registrationEndsAt
        lateRegistrationClosedAt
        isRegistrationOpen
        isLateRegistrationActive
        isSatellite
        isEvent
        registrationEntryMode
      }
      nextCursor
      hasNextPage
    }
  }
`;

const TOURNAMENT_LOBBY_LIVE_ROWS_QUERY = `
  query TournamentLobbyLiveRows($gameIdsIn: [ID!]!) {
    tournamentLobbyLiveRows(gameIdsIn: $gameIdsIn) {
      serverTime
      items {
        gameId
        status
        registeredPlayerCount
        activePlayerCount
        playersRemaining
        isViewerRegistered
        scheduledBreakActive
        addonBreakActive
        currentLevel
        updatedAt
        serverTime
      }
    }
  }
`;

const TOURNAMENT_LOBBY_DETAILS_QUERY = `
  query TournamentLobbyDetails($gameId: ID!) {
    tournamentLobbyDetails(gameId: $gameId) {
      serverTime
      tournament {
        gameId
        name
        imageName
        status
        registeredPlayerCount
        isSatellite
        isEvent
      }
      live {
        isViewerRegistered
        status
        activePlayerCount
        playersRemaining
        currentLevel
      }
      activeRooms {
        roomId
        tableName
        playerCount
        maximumPlayers
        isFinalTournamentTable
      }
      rankings {
        userId
        username
        countryCode
        balance
        position
        prize
      }
      blindStructureSnapshot
      currentBlindLevel
      tournamentExtraInformation
      payoutStructure
      ticketPrizes
      results
      playerList
    }
  }
`;

export type TournamentScheduleItem = {
    id: string;
    gameId: string;
    tournamentId: string;
    tournamentTemplateId?: string;
    name: string;
    imageName?: string | null;
    gameType: string;
    status: string;
    maxPlayers: number;
    registeredPlayerCount: number;
    buyIn: number;
    entryFee: number;
    prizePool: number;
    guaranteedPrizePool?: number;
    startTime: string;
    registrationEndsAt?: string | null;
    lateRegistrationClosedAt?: string | null;
    isRegistrationOpen?: boolean;
    isLateRegistrationActive?: boolean;
    isSatellite?: boolean;
    isEvent?: boolean;
    registrationEntryMode?: NonNullable<ITournamentState['registrationEntryMode']>;
};

type TournamentSchedulePage = {
    items: TournamentScheduleItem[];
    nextCursor?: string | null;
    hasNextPage?: boolean;
};

type TournamentScheduleResponse = {
    tournamentSchedule: TournamentSchedulePage;
};

type TournamentLobbyLiveRowsPage = {
    serverTime?: string;
    items?: ITournamentState[];
};

type TournamentLobbyDetailsPage = {
    serverTime?: string;
    tournament?: any;
    live?: any;
    activeRooms?: ITournamentState['activeRooms'];
    rankings?: ITournamentState['rankings'];
    blindStructureSnapshot?: ITournamentState['blindStructureSnapshot'];
    currentBlindLevel?: ITournamentState['currentBlindLevel'];
    tournamentExtraInformation?: ITournamentState['tournamentExtraInformation'];
    payoutStructure?: any;
    ticketPrizes?: ITournamentState['ticketPrizes'];
    results?: ITournamentState['results'];
    playerList?: ITournamentState['playerList'];
};

type TournamentLobbyLiveRowsResponse = {
    tournamentLobbyLiveRows: TournamentLobbyLiveRowsPage;
};

type TournamentLobbyDetailsResponse = {
    tournamentLobbyDetails: TournamentLobbyDetailsPage | null;
};

type TournamentScheduleVariables = {
    startTimeFrom: string;
    startTimeTo: string;
    statusIn?: string[];
    gameType?: string;
    nameContains?: string;
    isSatellite?: boolean;
    isEvent?: boolean;
    registrationEntryModeIn?: TournamentScheduleRegistrationEntryModeFilter[];
    first: number;
    after?: string;
};

export type TournamentScheduleStatusFilter = 'ALL' | 'REGISTRATION' | 'LATE_REGISTRATION' | 'STARTING' | 'RUNNING' | 'PAUSED' | 'COMPLETED' | 'CANCELLED';
export type TournamentSchedulePreset = 'OPEN' | 'CURRENT' | 'UPCOMING_SATELLITES' | 'UPCOMING_TICKET_ENTRY' | 'EVENTS' | 'CUSTOM';
export type TournamentScheduleRegistrationEntryModeFilter = 'CASH_ONLY' | 'TICKET_ONLY' | 'CASH_OR_TICKET';

export type TournamentScheduleFilters = {
    startTimeFrom: string;
    startTimeTo: string;
    statusIn?: string[];
    clientStatusFilter?: TournamentScheduleStatusFilter | 'OPEN';
    gameType?: string;
    nameContains?: string;
    isSatellite?: boolean;
    isEvent?: boolean;
    registrationEntryModeIn?: TournamentScheduleRegistrationEntryModeFilter[];
};

export type TournamentLobbyLiveRowsRequest = {
    gameIdsIn: string[];
};

export type TournamentLobbyDetailsRequest = {
    gameId: string;
};

export type TournamentScheduleFilterState = {
    nameContains: string;
    status: TournamentScheduleStatusFilter;
    date: string;
    preset: TournamentSchedulePreset;
};

export type TournamentScheduleSegment<T> = {
    items: T[];
    startIndex: number;
    segmentIndex: number;
    segmentCount: number;
    startItemNumber: number;
    endItemNumber: number;
    totalItems: number;
};

const TOURNAMENT_SCHEDULE_QUERY_KEY = ['tournamentSchedule'] as const;

export const tournamentScheduleQueryKeys = {
    all: TOURNAMENT_SCHEDULE_QUERY_KEY,
    lobby: (filters: TournamentScheduleFilters) => (
        [...TOURNAMENT_SCHEDULE_QUERY_KEY, 'lobby', filters] as const
    ),
    homeSummary: [...TOURNAMENT_SCHEDULE_QUERY_KEY, 'homeSummary'] as const,
};

export const invalidateTournamentScheduleQueries = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: tournamentScheduleQueryKeys.all });
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(TOURNAMENT_LOBBY_POLLING_REFRESH_EVENT));
    }
};

const toDateInputValue = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const shiftTournamentScheduleDate = (dateValue: string, days: number): string => {
    const parts = dateValue.split('-').map(Number);
    const [year, month, day] = parts;
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
        return dateValue;
    }

    const date = new Date(year, month - 1, day);
    date.setDate(date.getDate() + days);
    return toDateInputValue(date);
};

export const getDefaultTournamentScheduleFilterState = (now: Date = new Date()): TournamentScheduleFilterState => {
    return {
        nameContains: '',
        status: 'ALL',
        date: toDateInputValue(now),
        preset: 'CURRENT',
    };
};

export const getTournamentSchedulePresetState = (
    preset: Exclude<TournamentSchedulePreset, 'CUSTOM'>,
    currentDate: string
): TournamentScheduleFilterState => {
    return {
        nameContains: '',
        status: preset === 'CURRENT' ? 'ALL' : 'REGISTRATION',
        date: currentDate,
        preset,
    };
};

export const getTournamentScheduleSegmentFromStartIndex = <T,>(
    items: T[],
    requestedStartIndex: number
): TournamentScheduleSegment<T> => {
    const totalItems = items.length;
    const segmentCount = Math.max(1, Math.ceil(totalItems / TOURNAMENT_SCHEDULE_UI_SEGMENT_SIZE));
    const lastSegmentStartIndex = (segmentCount - 1) * TOURNAMENT_SCHEDULE_UI_SEGMENT_SIZE;
    const startIndex = requestedStartIndex >= totalItems
        ? lastSegmentStartIndex
        : Math.max(0, requestedStartIndex);
    const segmentItems = items.slice(startIndex, startIndex + TOURNAMENT_SCHEDULE_UI_SEGMENT_SIZE);
    const segmentIndex = Math.min(Math.floor(startIndex / TOURNAMENT_SCHEDULE_UI_SEGMENT_SIZE), segmentCount - 1);

    return {
        items: segmentItems,
        startIndex,
        segmentIndex,
        segmentCount,
        startItemNumber: totalItems === 0 ? 0 : startIndex + 1,
        endItemNumber: startIndex + segmentItems.length,
        totalItems,
    };
};

const isStaleClosedTournament = (tournament: ITournamentState, time: Date): boolean => {
    const startMs = new Date(tournament.startTime).getTime();
    return (
        Number.isFinite(startMs) &&
        startMs < time.getTime() &&
        (tournament.status === 'cancelled' || tournament.status === 'completed')
    );
};

export const getTournamentScheduleCurrentSegmentStartIndex = (
    tournaments: ITournamentState[],
    time: Date,
    staleRowsToKeep = 2
): number => {
    const firstUsefulIndex = tournaments.findIndex((tournament) => !isStaleClosedTournament(tournament, time));

    if (firstUsefulIndex >= 0) {
        return Math.max(0, firstUsefulIndex - staleRowsToKeep);
    }

    return Math.max(0, tournaments.length - staleRowsToKeep);
};

export const isTournamentScheduleDateToday = (date: string, now: Date = new Date()): boolean => {
    return date === toDateInputValue(now);
};

export const isTournamentScheduleCurrentTimeAnchoredPreset = (preset: TournamentSchedulePreset): boolean => (
    preset === 'OPEN' || preset === 'CURRENT'
);

export const formatTournamentScheduleSegmentLabel = (date: string, segment: Pick<TournamentScheduleSegment<unknown>, 'segmentIndex' | 'segmentCount'>): string => {
    if (segment.segmentCount <= 1) return date;
    return `${date} (${segment.segmentIndex + 1}/${segment.segmentCount})`;
};

export const formatTournamentScheduleGlobalSegmentLabel = (segment: Pick<TournamentScheduleSegment<unknown>, 'segmentIndex' | 'segmentCount'>): string => {
    return `${segment.segmentIndex + 1}/${segment.segmentCount}`;
};

export const isTournamentScheduleGlobalPreset = (preset: TournamentSchedulePreset): boolean => (
    preset === 'UPCOMING_SATELLITES' ||
    preset === 'UPCOMING_TICKET_ENTRY' ||
    preset === 'EVENTS'
);

const isTournamentScheduleTextSearchActive = (
    state: Pick<TournamentScheduleFilterState, 'nameContains'>
): boolean => state.nameContains.trim().length > 0;

export const isTournamentScheduleGlobalView = (
    state: Pick<TournamentScheduleFilterState, 'preset' | 'nameContains'>
): boolean => (
    isTournamentScheduleGlobalPreset(state.preset) ||
    isTournamentScheduleTextSearchActive(state)
);

const getScheduleStatusIn = (status: TournamentScheduleStatusFilter): string[] | undefined => {
    if (status === 'ALL') return undefined;
    if (status === 'LATE_REGISTRATION') return undefined;
    if (status === 'REGISTRATION') return ['REGISTRATION', 'SCHEDULED'];
    return [status];
};

const getPresetStatusIn = (preset: TournamentSchedulePreset): string[] | undefined => {
    if (preset === 'UPCOMING_SATELLITES' || preset === 'UPCOMING_TICKET_ENTRY' || preset === 'EVENTS') {
        return UPCOMING_TOURNAMENT_STATUS_FILTER;
    }
    return undefined;
};

const dateInputToIsoBoundary = (value: string, boundary: 'start' | 'end'): string => {
    const date = new Date(`${value}T00:00:00.000`);
    if (boundary === 'end') {
        date.setHours(23, 59, 59, 999);
    }
    return date.toISOString();
};

const getGlobalPresetWindow = (): Pick<TournamentScheduleFilters, 'startTimeFrom' | 'startTimeTo'> => {
    const start = new Date(`${toDateInputValue(new Date())}T00:00:00.000`);
    const end = new Date(start);
    end.setDate(end.getDate() + TOURNAMENT_SCHEDULE_GLOBAL_PRESET_WINDOW_DAYS);
    end.setHours(23, 59, 59, 999);

    return {
        startTimeFrom: start.toISOString(),
        startTimeTo: end.toISOString(),
    };
};

export const buildTournamentScheduleFilters = (state: TournamentScheduleFilterState): TournamentScheduleFilters => {
    const nameContains = state.nameContains.trim();
    const presetStatusIn = getPresetStatusIn(state.preset);
    const dateWindow = isTournamentScheduleGlobalView(state)
        ? getGlobalPresetWindow()
        : {
            startTimeFrom: dateInputToIsoBoundary(state.date, 'start'),
            startTimeTo: dateInputToIsoBoundary(state.date, 'end'),
        };

    return {
        ...dateWindow,
        statusIn: state.preset === 'OPEN'
            ? undefined
            : presetStatusIn ?? getScheduleStatusIn(state.status),
        clientStatusFilter: state.preset === 'OPEN'
            ? 'OPEN'
            : (state.status === 'LATE_REGISTRATION' ? 'LATE_REGISTRATION' : undefined),
        gameType: 'TEXAS',
        nameContains: nameContains.length > 0 ? nameContains : undefined,
        isSatellite: state.preset === 'UPCOMING_SATELLITES' ? true : undefined,
        isEvent: state.preset === 'EVENTS' ? true : undefined,
        registrationEntryModeIn: state.preset === 'UPCOMING_TICKET_ENTRY'
            ? ['TICKET_ONLY', 'CASH_OR_TICKET']
            : undefined,
    };
};

export const buildTournamentLobbyLiveRowsRequest = (
    visibleTournaments: ITournamentState[]
): TournamentLobbyLiveRowsRequest | null => {
    const gameIdsIn = getTournamentGameIds(visibleTournaments);
    if (gameIdsIn.length === 0) return null;

    return { gameIdsIn };
};

export const buildTournamentLobbyDetailsRequest = (
    modalTournamentId?: string | null
): TournamentLobbyDetailsRequest | null => {
    return modalTournamentId ? { gameId: modalTournamentId } : null;
};

const requestTournamentLobbyLiveRows = async (
    request: TournamentLobbyLiveRowsRequest,
    signal?: AbortSignal
): Promise<TournamentLobbyLiveRowsPage> => {
    const response = await pokerGraphqlRequest<TournamentLobbyLiveRowsResponse, TournamentLobbyLiveRowsRequest>({
        query: TOURNAMENT_LOBBY_LIVE_ROWS_QUERY,
        variables: request,
        signal,
    });

    return response.tournamentLobbyLiveRows;
};

const requestTournamentLobbyDetails = async (
    request: TournamentLobbyDetailsRequest,
    signal?: AbortSignal
): Promise<TournamentLobbyDetailsPage | null> => {
    const response = await pokerGraphqlRequest<TournamentLobbyDetailsResponse, TournamentLobbyDetailsRequest>({
        query: TOURNAMENT_LOBBY_DETAILS_QUERY,
        variables: request,
        signal,
    });

    return response.tournamentLobbyDetails;
};

const requestTournamentSchedulePage = async (
    variables: TournamentScheduleVariables,
    signal?: AbortSignal
): Promise<TournamentSchedulePage> => {
    const response = await pokerGraphqlRequest<TournamentScheduleResponse, TournamentScheduleVariables>({
        query: TOURNAMENT_SCHEDULE_QUERY,
        variables,
        signal,
    });

    return response.tournamentSchedule;
};

const normalizeScheduleStatus = (status: string): ITournamentState['status'] | null => {
    const normalized = status.trim().toLowerCase();
    if (normalized === 'scheduled') return 'registration';

    if (
        normalized === 'registering' ||
        normalized === 'registration' ||
        normalized === 'late_registration' ||
        normalized === 'starting' ||
        normalized === 'running' ||
        normalized === 'paused' ||
        normalized === 'completed' ||
        normalized === 'cancelled'
    ) {
        return normalized;
    }
    return null;
};

const normalizeLobbyTournamentStatus = (status: ITournamentState['status']): ITournamentState['status'] => {
    return status === 'scheduled' ? 'registration' : status;
};

const isTerminalLobbyTournamentStatus = (status: ITournamentState['status']): boolean => {
    return status === 'completed' || status === 'cancelled';
};

const normalizeScheduleGameType = (gameType: string): ITournamentState['type'] | null => {
    const normalized = gameType.trim().toLowerCase();
    if (normalized === 'texas' || normalized === 'aof' || normalized === 'omaha') {
        return normalized;
    }
    return null;
};

export const normalizeTournamentScheduleItem = (item: TournamentScheduleItem): ITournamentState | null => {
    if (!item?.gameId || !item?.tournamentId || !item?.name || !item?.startTime) return null;

    const status = normalizeScheduleStatus(item.status);
    const type = normalizeScheduleGameType(item.gameType);
    if (!status || !type) return null;

    return {
        tournamentId: item.tournamentId,
        gameId: item.gameId,
        tournamentTemplateId: item.tournamentTemplateId,
        name: item.name,
        imageName: typeof item.imageName === 'string' && item.imageName.length > 0 ? item.imageName : undefined,
        status,
        registeredPlayers: [],
        playerList: [],
        registeredPlayerCount: item.registeredPlayerCount,
        startTime: item.startTime,
        tables: [],
        currentLevel: 0,
        blindStructureSnapshot: [],
        buyIn: item.buyIn,
        entryFee: item.entryFee,
        prizePool: item.prizePool,
        guaranteedPrizePool: item.guaranteedPrizePool,
        minPlayers: 0,
        maxPlayers: item.maxPlayers,
        type,
        registrationEndsAt: item.registrationEndsAt,
        lateRegistrationClosedAt: item.lateRegistrationClosedAt,
        isRegistrationOpen: item.isRegistrationOpen,
        isLateRegistrationActive: item.isLateRegistrationActive,
        isSatellite: item.isSatellite === true,
        isEvent: item.isEvent === true,
        registrationEntryMode: item.registrationEntryMode,
        currency: '',
        currencyType: 'fiat',
        rankings: [],
        activeRooms: [],
    };
};

const getTournamentIdentity = (tournament: ITournamentState): string | null => {
    return tournament.gameId || tournament.tournamentId || null;
};

export const getTournamentGameIds = (tournaments: ITournamentState[]): string[] => {
    return tournaments
        .map(getTournamentIdentity)
        .filter((id): id is string => typeof id === 'string' && id.length > 0);
};

export const getTournamentLobbyLiveRowsRequestKey = (request: TournamentLobbyLiveRowsRequest): string => {
    return getTournamentGameIdsKey(request.gameIdsIn);
};

export const getTournamentLobbyDetailsRequestKey = (request: TournamentLobbyDetailsRequest): string => {
    return request.gameId;
};

export const getTournamentGameIdsKey = (gameIds: string[]): string => {
    return gameIds.join('|');
};

export const getTournamentLobbyLiveRowsForRequest = (
    request: TournamentLobbyLiveRowsRequest | null,
    page?: TournamentLobbyLiveRowsPage
): ITournamentState[] => {
    if (!request || !page) return [];
    return Array.isArray(page.items) ? page.items : [];
};

export const getTournamentLobbyDetailsTournament = (
    page?: TournamentLobbyDetailsPage | null
): ITournamentState | null => {
    return page?.tournament?.gameId ? page.tournament as ITournamentState : null;
};

export const getTournamentLobbyDebugGameId = (): string | null => {
    const envGameId = import.meta.env.VITE_TOURNAMENT_LOBBY_DEBUG_GAME_ID;
    if (typeof envGameId === 'string' && envGameId.length > 0) return envGameId;

    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(TOURNAMENT_LOBBY_DEBUG_GAME_ID_STORAGE_KEY);
};

export const logTournamentLobbyDebug = (
    label: string,
    payload: Record<string, unknown>
): void => {
    if (!getTournamentLobbyDebugGameId()) return;
    console.debug(`[tournament-lobby:${label}]`, payload);
};

export const logTournamentLobbyTournamentDebug = (
    label: string,
    tournament: ITournamentState | null | undefined,
    extra: Record<string, unknown> = {}
): void => {
    const debugGameId = getTournamentLobbyDebugGameId();
    if (!debugGameId || !tournament || getTournamentIdentity(tournament) !== debugGameId) return;

    console.debug(`[tournament-lobby:${label}]`, {
        gameId: debugGameId,
        status: tournament.status,
        activeRooms: tournament.activeRooms,
        activeRoomsLength: tournament.activeRooms?.length ?? 0,
        tableStructure: (tournament as any).tableStructure,
        activePlayerCount: tournament.activePlayerCount,
        playersRemaining: tournament.playersRemaining,
        remainingPlayerCount: tournament.remainingPlayerCount,
        projectionVersion: tournament.projectionVersion,
        serverTime: tournament.serverTime,
        updatedAt: tournament.updatedAt ?? null,
        ...extra,
    });
};

const logRawTournamentScheduleItem = (item: TournamentScheduleItem): void => {
    const debugGameId = getTournamentLobbyDebugGameId();
    if (!debugGameId || item.gameId !== debugGameId) return;

    console.debug('[tournament-lobby:graphql-row]', {
        gameId: debugGameId,
        status: item.status,
        activeRooms: (item as any).activeRooms,
        tableStructure: (item as any).tableStructure,
        activePlayerCount: (item as any).activePlayerCount,
        playersRemaining: (item as any).playersRemaining,
        remainingPlayerCount: (item as any).remainingPlayerCount,
        projectionVersion: (item as any).projectionVersion,
        serverTime: (item as any).serverTime,
        updatedAt: (item as any).updatedAt ?? null,
        endTime: (item as any).endTime ?? null,
    });
};

const getTournamentScheduleGraphqlFilters = (filters: TournamentScheduleFilters): Omit<TournamentScheduleFilters, 'clientStatusFilter'> => {
    const { clientStatusFilter: _clientStatusFilter, ...graphqlFilters } = filters;
    return graphqlFilters;
};

const splitTournamentScheduleFiltersIntoGraphqlWindows = (
    filters: TournamentScheduleFilters
): TournamentScheduleFilters[] => {
    const startMs = Date.parse(filters.startTimeFrom);
    const endMs = Date.parse(filters.startTimeTo);
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
        return [filters];
    }

    const maxWindowMs = TOURNAMENT_SCHEDULE_GRAPHQL_MAX_WINDOW_DAYS * 24 * 60 * 60 * 1000;
    if (endMs - startMs < maxWindowMs) {
        return [filters];
    }

    const windows: TournamentScheduleFilters[] = [];
    let windowStartMs = startMs;
    while (windowStartMs <= endMs) {
        const windowEndMs = Math.min(endMs, windowStartMs + maxWindowMs - 1);
        windows.push({
            ...filters,
            startTimeFrom: new Date(windowStartMs).toISOString(),
            startTimeTo: new Date(windowEndMs).toISOString(),
        });
        windowStartMs = windowEndMs + 1;
    }

    return windows;
};

const requestTournamentScheduleWindowForFilters = async (
    filters: TournamentScheduleFilters,
    signal?: AbortSignal
): Promise<TournamentScheduleItem[]> => {
    const items: TournamentScheduleItem[] = [];
    const queryWindows = splitTournamentScheduleFiltersIntoGraphqlWindows(filters);

    for (const queryWindow of queryWindows) {
        let after: string | undefined;

        do {
            const page = await requestTournamentSchedulePage({
                ...getTournamentScheduleGraphqlFilters(queryWindow),
                first: TOURNAMENT_SCHEDULE_PAGE_SIZE,
                after,
            }, signal);

            if (Array.isArray(page.items)) {
                page.items.forEach(logRawTournamentScheduleItem);
                items.push(...page.items);
            }

            after = page.hasNextPage && page.nextCursor ? page.nextCursor : undefined;
        } while (after);
    }

    return items;
};

const matchesTournamentScheduleClientStatusFilter = (
    tournament: ITournamentState,
    statusFilter?: TournamentScheduleStatusFilter | 'OPEN'
): boolean => {
    if (!statusFilter || statusFilter === 'ALL') return true;

    if (statusFilter === 'OPEN') {
        return (
            tournament.status === 'registration' ||
            tournament.status === 'scheduled' ||
            tournament.status === 'registering' ||
            tournament.status === 'late_registration' ||
            tournament.isRegistrationOpen === true ||
            tournament.isLateRegistrationActive === true
        );
    }

    if (statusFilter === 'LATE_REGISTRATION') {
        return tournament.status === 'late_registration' || tournament.isLateRegistrationActive === true;
    }

    return true;
};

const normalizeTournamentLobbyStatus = (status: unknown): ITournamentState['status'] => {
    if (typeof status !== 'string') return 'registration';
    return normalizeScheduleStatus(status) || 'registration';
};

const normalizeTournamentLobbyGameType = (gameType: unknown): ITournamentState['type'] => {
    if (typeof gameType !== 'string') return 'texas';
    return normalizeScheduleGameType(gameType) || 'texas';
};

const numberOrZero = (value: unknown): number => (
    typeof value === 'number' && Number.isFinite(value) ? value : 0
);

const addCountryCodeSource = (
    countryCodeById: Map<string, string>,
    countryCodeByUsername: Map<string, string | null>,
    source: unknown
): void => {
    if (!source || typeof source !== 'object') return;

    const record = source as Record<string, unknown>;
    const countryCode = typeof record.countryCode === 'string' && record.countryCode.length > 0
        ? record.countryCode
        : (typeof record.country === 'string' && record.country.length > 0 ? record.country : undefined);
    if (!countryCode) return;

    const userId = typeof record.userId === 'string' ? record.userId : undefined;
    const playerId = typeof record.playerId === 'string' ? record.playerId : undefined;
    const username = typeof record.username === 'string' ? record.username : undefined;

    if (userId) countryCodeById.set(userId, countryCode);
    if (playerId) countryCodeById.set(playerId, countryCode);
    if (username) {
        const previousCountryCode = countryCodeByUsername.get(username);
        if (previousCountryCode === undefined) {
            countryCodeByUsername.set(username, countryCode);
        } else if (previousCountryCode !== countryCode) {
            countryCodeByUsername.set(username, null);
        }
    }
};

const normalizeTournamentRankings = (
    rankings: unknown,
    playerList?: ITournamentState['playerList'],
    results?: ITournamentState['results']
): ITournamentState['rankings'] => {
    if (!Array.isArray(rankings)) return [];

    const countryCodeById = new Map<string, string>();
    const countryCodeByUsername = new Map<string, string | null>();
    playerList?.forEach((player) => addCountryCodeSource(countryCodeById, countryCodeByUsername, player));
    results?.forEach((result) => addCountryCodeSource(countryCodeById, countryCodeByUsername, result));

    return rankings.map((ranking: any) => {
        const userId = typeof ranking.userId === 'string' ? ranking.userId : undefined;
        const playerId = typeof ranking.playerId === 'string' ? ranking.playerId : undefined;
        const username = typeof ranking.username === 'string' ? ranking.username : undefined;
        const countryCodeForUsername = username ? countryCodeByUsername.get(username) : undefined;
        const countryCode = typeof ranking.countryCode === 'string' && ranking.countryCode.length > 0
            ? ranking.countryCode
            : (
                (typeof ranking.country === 'string' && ranking.country.length > 0 ? ranking.country : undefined) ||
                (userId ? countryCodeById.get(userId) : undefined) ||
                (playerId ? countryCodeById.get(playerId) : undefined) ||
                (typeof countryCodeForUsername === 'string' ? countryCodeForUsername : undefined)
            );

        return {
            ...ranking,
            countryCode,
            eliminationTime: ranking.eliminationTime || null,
            registrationTime: ranking.registrationTime || null,
            chipsBeforeElimination: typeof ranking.chipsBeforeElimination === 'number' ? ranking.chipsBeforeElimination : null,
        };
    });
};

const normalizeTournamentLobbyLiveRow = (row: any): ITournamentState => {
    const status = normalizeTournamentLobbyStatus(row?.status);
    return {
        tournamentId: typeof row?.tournamentId === 'string' ? row.tournamentId : (row?.gameId || ''),
        gameId: typeof row?.gameId === 'string' ? row.gameId : (row?.tournamentId || ''),
        name: '',
        status,
        isUserRegistered: row?.isViewerRegistered === true,
        type: 'texas',
        registeredPlayers: [],
        playerList: [],
        registeredPlayerCount: numberOrZero(row?.registeredPlayerCount),
        playerCount: typeof row?.playerCount === 'number' ? row.playerCount : undefined,
        totalRegisteredPlayerCount: typeof row?.totalRegisteredPlayerCount === 'number' ? row.totalRegisteredPlayerCount : undefined,
        activePlayerCount: typeof row?.activePlayerCount === 'number' ? row.activePlayerCount : undefined,
        remainingPlayerCount: typeof row?.remainingPlayerCount === 'number' ? row.remainingPlayerCount : undefined,
        playersRemaining: typeof row?.playersRemaining === 'number' ? row.playersRemaining : undefined,
        startTime: typeof row?.startTime === 'string' ? row.startTime : '',
        tables: [],
        currentLevel: numberOrZero(row?.currentLevel),
        scheduledBreakActive: typeof row?.scheduledBreakActive === 'boolean' ? row.scheduledBreakActive : undefined,
        addonBreakActive: typeof row?.addonBreakActive === 'boolean' ? row.addonBreakActive : undefined,
        blindStructureSnapshot: [],
        buyIn: 0,
        entryFee: 0,
        prizePool: numberOrZero(row?.prizePool),
        guaranteedPrizePool: typeof row?.guaranteedPrizePool === 'number' ? row.guaranteedPrizePool : undefined,
        minPlayers: numberOrZero(row?.minPlayers),
        maxPlayers: numberOrZero(row?.maxPlayers),
        isRegistrationOpen: typeof row?.isRegistrationOpen === 'boolean' ? row.isRegistrationOpen : undefined,
        isLateRegistrationActive: typeof row?.isLateRegistrationActive === 'boolean' ? row.isLateRegistrationActive : undefined,
        registrationEndsAt: typeof row?.registrationEndsAt === 'string' || row?.registrationEndsAt === null ? row.registrationEndsAt : undefined,
        lateRegistrationClosedAt: typeof row?.lateRegistrationClosedAt === 'string' || row?.lateRegistrationClosedAt === null ? row.lateRegistrationClosedAt : undefined,
        currency: '',
        currencyType: 'fiat',
        activeRooms: [],
        updatedAt: typeof row?.updatedAt === 'string' ? row.updatedAt : undefined,
        serverTime: typeof row?.serverTime === 'string' ? row.serverTime : undefined,
    };
};

const normalizeTournamentLobbyLiveRowsPage = (page: TournamentLobbyLiveRowsPage): TournamentLobbyLiveRowsPage => ({
    ...page,
    items: Array.isArray(page.items)
        ? page.items.map(normalizeTournamentLobbyLiveRow)
        : [],
});

const normalizeTournamentLobbyDetailsPage = (page: TournamentLobbyDetailsPage | null): TournamentLobbyDetailsPage | null => {
    if (!page?.tournament?.gameId) return page;

    const sourceTournament = page.tournament;
    const live = page.live || {};
    const status = normalizeTournamentLobbyStatus(live.status || sourceTournament.status);
    const isTerminal = isTerminalLobbyTournamentStatus(status);

    const tournament: ITournamentState = {
        ...sourceTournament,
        tournamentId: typeof sourceTournament.tournamentId === 'string' ? sourceTournament.tournamentId : sourceTournament.gameId,
        gameId: sourceTournament.gameId,
        name: typeof sourceTournament.name === 'string' ? sourceTournament.name : '',
        imageName: typeof sourceTournament.imageName === 'string' && sourceTournament.imageName.length > 0
            ? sourceTournament.imageName
            : undefined,
        status,
        isUserRegistered: live?.isViewerRegistered === true,
        isSatellite: sourceTournament.isSatellite === true,
        isEvent: sourceTournament.isEvent === true,
        type: normalizeTournamentLobbyGameType(sourceTournament.gameType || sourceTournament.type),
        registeredPlayers: [],
        playerList: Array.isArray(page.playerList) ? page.playerList : [],
        registeredPlayerCount: numberOrZero(sourceTournament.registeredPlayerCount),
        playerCount: typeof live.playerCount === 'number' ? live.playerCount : undefined,
        totalRegisteredPlayerCount: typeof live.totalRegisteredPlayerCount === 'number' ? live.totalRegisteredPlayerCount : undefined,
        activePlayerCount: isTerminal ? 0 : (typeof live.activePlayerCount === 'number' ? live.activePlayerCount : undefined),
        remainingPlayerCount: isTerminal ? 0 : (typeof live.remainingPlayerCount === 'number' ? live.remainingPlayerCount : undefined),
        playersRemaining: isTerminal ? 0 : (typeof live.playersRemaining === 'number' ? live.playersRemaining : undefined),
        startTime: typeof sourceTournament.startTime === 'string' ? sourceTournament.startTime : '',
        tables: [],
        currentLevel: numberOrZero(live.currentLevel ?? sourceTournament.currentLevel),
        blindStructureSnapshot: Array.isArray(page.blindStructureSnapshot) ? page.blindStructureSnapshot : [],
        buyIn: numberOrZero(sourceTournament.buyIn),
        entryFee: numberOrZero(sourceTournament.entryFee),
        prizePool: numberOrZero(sourceTournament.prizePool),
        guaranteedPrizePool: typeof sourceTournament.guaranteedPrizePool === 'number' ? sourceTournament.guaranteedPrizePool : undefined,
        minPlayers: numberOrZero(sourceTournament.minPlayers),
        maxPlayers: numberOrZero(sourceTournament.maxPlayers),
        currency: typeof sourceTournament.currency === 'string' ? sourceTournament.currency : '',
        currencyType: sourceTournament.currencyType === 'crypto' ? 'crypto' : 'fiat',
        activeRooms: isTerminal ? [] : (Array.isArray(page.activeRooms) ? page.activeRooms : []),
        rankings: normalizeTournamentRankings(page.rankings, page.playerList, page.results),
        results: Array.isArray(page.results) ? page.results : [],
        ticketPrizes: Array.isArray(page.ticketPrizes) ? page.ticketPrizes : undefined,
        currentBlindLevel: page.currentBlindLevel,
        tournamentExtraInformation: page.tournamentExtraInformation,
        payoutStructure: page.payoutStructure,
        serverTime: typeof page.serverTime === 'string' ? page.serverTime : undefined,
    };

    return {
        ...page,
        tournament,
    };
};

const logTournamentLobbyLiveRowsPage = (
    label: string,
    request: TournamentLobbyLiveRowsRequest,
    page: TournamentLobbyLiveRowsPage
): void => {
    const debugGameId = getTournamentLobbyDebugGameId();
    if (!debugGameId) return;

    const row = (page.items || []).find((tournament) => getTournamentIdentity(tournament) === debugGameId);
    console.debug(`[tournament-lobby:${label}]`, {
        gameId: debugGameId,
        present: Boolean(row),
        request,
        responseServerTime: page.serverTime,
        status: row?.status,
        activePlayerCount: row?.activePlayerCount,
        playersRemaining: row?.playersRemaining,
        remainingPlayerCount: row?.remainingPlayerCount,
        isUserRegistered: row?.isUserRegistered,
        serverTime: row?.serverTime,
        updatedAt: row?.updatedAt ?? null,
    });
};

const logTournamentLobbyMerge = (
    scheduled: ITournamentState,
    live: ITournamentState,
    merged: ITournamentState,
    sources: Record<string, string>
) => {
    const debugGameId = getTournamentLobbyDebugGameId();
    if (!debugGameId || getTournamentIdentity(merged) !== debugGameId) return;

    console.debug('[tournament-lobby-merge]', {
        gameId: debugGameId,
        sourceGraphqlStatus: scheduled.status,
        sourceLiveStatus: live.status,
        mergedStatus: merged.status,
        activeRooms: merged.activeRooms,
        activeRoomsLength: merged.activeRooms?.length ?? 0,
        tableStructure: (merged as any).tableStructure,
        activePlayerCount: merged.activePlayerCount,
        playersRemaining: merged.playersRemaining,
        remainingPlayerCount: merged.remainingPlayerCount,
        projectionVersion: merged.projectionVersion,
        serverTime: merged.serverTime,
        updatedAt: merged.updatedAt ?? null,
        sources,
    });
};

const mergeTournamentRows = (scheduled: ITournamentState, live: ITournamentState): ITournamentState => {
    const liveStatus = normalizeLobbyTournamentStatus(live.status);
    const liveIsTerminal = isTerminalLobbyTournamentStatus(liveStatus);
    const merged = {
        ...scheduled,
        ...live,
        tournamentId: live.tournamentId || scheduled.tournamentId,
        gameId: live.gameId || scheduled.gameId,
        name: live.name || scheduled.name,
        status: liveStatus,
        startTime: live.startTime || scheduled.startTime,
        buyIn: scheduled.buyIn,
        entryFee: scheduled.entryFee,
        prizePool: scheduled.prizePool,
        guaranteedPrizePool: scheduled.guaranteedPrizePool,
        minPlayers: scheduled.minPlayers,
        maxPlayers: scheduled.maxPlayers,
        type: scheduled.type,
        isSatellite: scheduled.isSatellite,
        isEvent: scheduled.isEvent,
        registrationEntryMode: scheduled.registrationEntryMode,
        isRegistrationOpen: scheduled.isRegistrationOpen,
        isLateRegistrationActive: scheduled.isLateRegistrationActive,
        registrationEndsAt: scheduled.registrationEndsAt,
        lateRegistrationClosedAt: scheduled.lateRegistrationClosedAt,
        currency: scheduled.currency,
        currencyType: scheduled.currencyType,
        playerList: Array.isArray(live.playerList) ? live.playerList : scheduled.playerList,
        registeredPlayers: Array.isArray(live.registeredPlayers) ? live.registeredPlayers : scheduled.registeredPlayers,
        rankings: Array.isArray(live.rankings) ? live.rankings : scheduled.rankings,
        playerCount: typeof live.playerCount === 'number' ? live.playerCount : scheduled.playerCount,
        totalRegisteredPlayerCount: typeof live.totalRegisteredPlayerCount === 'number'
            ? live.totalRegisteredPlayerCount
            : scheduled.totalRegisteredPlayerCount,
        activeRooms: liveIsTerminal
            ? []
            : (Array.isArray(live.activeRooms) ? live.activeRooms : []),
        blindStructureSnapshot: Array.isArray(live.blindStructureSnapshot) ? live.blindStructureSnapshot : scheduled.blindStructureSnapshot,
        activePlayerCount: liveIsTerminal ? 0 : live.activePlayerCount,
        playersRemaining: liveIsTerminal ? 0 : live.playersRemaining,
        remainingPlayerCount: liveIsTerminal ? 0 : live.remainingPlayerCount,
        endTime: live.endTime ?? scheduled.endTime,
        updatedAt: live.updatedAt ?? scheduled.updatedAt,
        isUserRegistered: typeof live.isUserRegistered === 'boolean'
            ? live.isUserRegistered
            : scheduled.isUserRegistered,
    };

    logTournamentLobbyMerge(scheduled, live, merged, {
        status: 'lobbyDetails',
        activeRooms: liveIsTerminal ? 'lobbyDetails-terminal-clear' : (Array.isArray(live.activeRooms) ? 'lobbyDetails' : 'empty'),
        activePlayerCount: liveIsTerminal ? 'lobbyDetails-terminal-clear' : 'lobbyDetails',
        playersRemaining: liveIsTerminal ? 'lobbyDetails-terminal-clear' : 'lobbyDetails',
    });
    return merged;
};

const mergeTournamentListRow = (scheduled: ITournamentState, live: ITournamentState): ITournamentState => {
    const liveStatus = normalizeLobbyTournamentStatus(live.status);
    const isTerminal = isTerminalLobbyTournamentStatus(liveStatus);
    const merged: ITournamentState = {
        ...scheduled,
        status: liveStatus,
        registeredPlayerCount: live.registeredPlayerCount,
        activePlayerCount: isTerminal ? 0 : live.activePlayerCount,
        playersRemaining: isTerminal ? 0 : live.playersRemaining,
        isUserRegistered: live.isUserRegistered,
        scheduledBreakActive: live.scheduledBreakActive,
        addonBreakActive: live.addonBreakActive,
        currentLevel: live.currentLevel,
        updatedAt: live.updatedAt ?? scheduled.updatedAt,
        serverTime: live.serverTime,
    };

    logTournamentLobbyMerge(scheduled, live, merged, {
        status: 'lobbyLiveRows',
        activeRooms: 'schedule-base',
        activePlayerCount: isTerminal ? 'lobbyLiveRows-terminal-clear' : 'lobbyLiveRows',
        playersRemaining: isTerminal ? 'lobbyLiveRows-terminal-clear' : 'lobbyLiveRows',
        isUserRegistered: 'lobbyLiveRows',
    });
    return merged;
};

export const mergeScheduleTournamentsWithRealtime = (
    scheduledTournaments: ITournamentState[],
    liveTournaments: ITournamentState[]
): ITournamentState[] => {
    const byId = new Map<string, ITournamentState>();

    scheduledTournaments.forEach((tournament) => {
        const id = getTournamentIdentity(tournament);
        if (id) byId.set(id, tournament);
    });

    liveTournaments.forEach((tournament) => {
        const id = getTournamentIdentity(tournament);
        if (!id) return;

        const scheduled = byId.get(id);
        if (scheduled) {
            byId.set(id, mergeTournamentRows(scheduled, tournament));
        }
    });

    const debugGameId = getTournamentLobbyDebugGameId();
    const hasDebugLiveRow = Boolean(debugGameId && liveTournaments.some((tournament) => getTournamentIdentity(tournament) === debugGameId));
    const debugMergedRow = debugGameId ? byId.get(debugGameId) : null;
    if (debugGameId && debugMergedRow && !hasDebugLiveRow) {
        console.debug('[tournament-lobby-merge]', {
            gameId: debugGameId,
            sourceGraphqlStatus: debugMergedRow.status,
            sourceLiveStatus: null,
            mergedStatus: debugMergedRow.status,
            activeRoomsLength: debugMergedRow.activeRooms?.length ?? 0,
            updatedAt: debugMergedRow.updatedAt ?? null,
        });
    }

    return Array.from(byId.values());
};

export const mergeScheduleTournamentsWithLobbyLiveRows = (
    scheduledTournaments: ITournamentState[],
    liveTournaments: ITournamentState[]
): ITournamentState[] => {
    const byId = new Map<string, ITournamentState>();

    scheduledTournaments.forEach((tournament) => {
        const id = getTournamentIdentity(tournament);
        if (id) byId.set(id, tournament);
    });

    liveTournaments.forEach((tournament) => {
        const id = getTournamentIdentity(tournament);
        if (!id) return;

        const scheduled = byId.get(id);
        if (scheduled) {
            byId.set(id, mergeTournamentListRow(scheduled, tournament));
        }
    });

    return Array.from(byId.values());
};

export const useTournamentLobbyLiveRows = (request: TournamentLobbyLiveRowsRequest | null) => {
    const requestKey = useMemo(
        () => request ? getTournamentLobbyLiveRowsRequestKey(request) : '',
        [request]
    );
    const sequenceRef = useRef(0);
    const abortRef = useRef<AbortController | null>(null);
    const requestRef = useRef<TournamentLobbyLiveRowsRequest | null>(request);
    const [state, setState] = useState<{
        data?: TournamentLobbyLiveRowsPage;
        isLoading: boolean;
        isFetching: boolean;
        error: unknown | null;
    }>({
        data: undefined,
        isLoading: Boolean(request),
        isFetching: false,
        error: null,
    });

    useEffect(() => {
        requestRef.current = request;
    }, [requestKey, request]);

    const fetchLiveRows = useCallback(async (options: { resetData?: boolean } = {}) => {
        const activeRequest = requestRef.current;

        if (!activeRequest) {
            abortRef.current?.abort();
            abortRef.current = null;
            sequenceRef.current += 1;
            setState({ data: undefined, isLoading: false, isFetching: false, error: null });
            return;
        }

        const sequence = sequenceRef.current + 1;
        sequenceRef.current = sequence;
        abortRef.current?.abort();

        const controller = new AbortController();
        abortRef.current = controller;

        setState((previous) => ({
            data: options.resetData ? undefined : previous.data,
            isLoading: options.resetData === true,
            isFetching: true,
            error: null,
        }));

        logTournamentLobbyDebug('lobby-live-rows-request', {
            request: activeRequest,
            requestKey,
            sequence,
        });

        try {
            const page = normalizeTournamentLobbyLiveRowsPage(await requestTournamentLobbyLiveRows(activeRequest, controller.signal));
            if (controller.signal.aborted || sequence !== sequenceRef.current) return;

            logTournamentLobbyLiveRowsPage('lobby-live-rows-response', activeRequest, page);
            setState({
                data: page,
                isLoading: false,
                isFetching: false,
                error: null,
            });
        } catch (error) {
            if (controller.signal.aborted || sequence !== sequenceRef.current) return;

            logTournamentLobbyDebug('lobby-live-rows-error', {
                request: activeRequest,
                requestKey,
                error: error instanceof Error ? error.message : String(error),
            });
            setState({
                data: undefined,
                isLoading: false,
                isFetching: false,
                error,
            });
        }
    }, [requestKey]);

    useEffect(() => {
        if (!request) {
            abortRef.current?.abort();
            abortRef.current = null;
            sequenceRef.current += 1;
            setState({ data: undefined, isLoading: false, isFetching: false, error: null });
            return;
        }

        void fetchLiveRows({ resetData: true });

        const interval = window.setInterval(() => {
            if (document.visibilityState === 'visible') {
                void fetchLiveRows();
            }
        }, TOURNAMENT_LOBBY_POLL_INTERVAL_MS);

        const refetchWhenVisible = () => {
            if (document.visibilityState === 'visible') {
                void fetchLiveRows();
            }
        };

        document.addEventListener('visibilitychange', refetchWhenVisible);
        window.addEventListener('focus', refetchWhenVisible);
        window.addEventListener(TOURNAMENT_LOBBY_POLLING_REFRESH_EVENT, refetchWhenVisible);

        return () => {
            window.clearInterval(interval);
            document.removeEventListener('visibilitychange', refetchWhenVisible);
            window.removeEventListener('focus', refetchWhenVisible);
            window.removeEventListener(TOURNAMENT_LOBBY_POLLING_REFRESH_EVENT, refetchWhenVisible);
            abortRef.current?.abort();
            abortRef.current = null;
            sequenceRef.current += 1;
        };
    }, [requestKey, fetchLiveRows]);

    return {
        ...state,
        refetch: fetchLiveRows,
    };
};

export const useTournamentLobbyDetails = (request: TournamentLobbyDetailsRequest | null) => {
    const requestKey = useMemo(
        () => request ? getTournamentLobbyDetailsRequestKey(request) : '',
        [request]
    );
    const sequenceRef = useRef(0);
    const abortRef = useRef<AbortController | null>(null);
    const [state, setState] = useState<{
        data?: TournamentLobbyDetailsPage | null;
        isLoading: boolean;
        isFetching: boolean;
        error: unknown | null;
    }>({
        data: undefined,
        isLoading: Boolean(request),
        isFetching: false,
        error: null,
    });

    const fetchDetails = useCallback(async (options: { resetData?: boolean } = {}) => {
        if (!request) {
            abortRef.current?.abort();
            abortRef.current = null;
            sequenceRef.current += 1;
            setState({ data: undefined, isLoading: false, isFetching: false, error: null });
            return;
        }

        const sequence = sequenceRef.current + 1;
        sequenceRef.current = sequence;
        abortRef.current?.abort();

        const controller = new AbortController();
        abortRef.current = controller;

        setState((previous) => ({
            data: options.resetData ? undefined : previous.data,
            isLoading: options.resetData === true,
            isFetching: true,
            error: null,
        }));

        logTournamentLobbyDebug('lobby-details-request', {
            request,
            requestKey,
            sequence,
        });

        try {
            const page = normalizeTournamentLobbyDetailsPage(await requestTournamentLobbyDetails(request, controller.signal));
            if (controller.signal.aborted || sequence !== sequenceRef.current) return;

            logTournamentLobbyTournamentDebug('lobby-details-response', page?.tournament, {
                request,
                responseServerTime: page?.serverTime,
            });
            setState({
                data: page,
                isLoading: false,
                isFetching: false,
                error: null,
            });
        } catch (error) {
            if (controller.signal.aborted || sequence !== sequenceRef.current) return;

            logTournamentLobbyDebug('lobby-details-error', {
                request,
                requestKey,
                error: error instanceof Error ? error.message : String(error),
            });
            setState({
                data: undefined,
                isLoading: false,
                isFetching: false,
                error,
            });
        }
    }, [request, requestKey]);

    useEffect(() => {
        if (!request) {
            abortRef.current?.abort();
            abortRef.current = null;
            sequenceRef.current += 1;
            setState({ data: undefined, isLoading: false, isFetching: false, error: null });
            return;
        }

        void fetchDetails({ resetData: true });

        const interval = window.setInterval(() => {
            if (document.visibilityState === 'visible') {
                void fetchDetails();
            }
        }, TOURNAMENT_LOBBY_POLL_INTERVAL_MS);

        const refetchWhenVisible = () => {
            if (document.visibilityState === 'visible') {
                void fetchDetails();
            }
        };

        document.addEventListener('visibilitychange', refetchWhenVisible);
        window.addEventListener('focus', refetchWhenVisible);
        window.addEventListener(TOURNAMENT_LOBBY_POLLING_REFRESH_EVENT, refetchWhenVisible);

        return () => {
            window.clearInterval(interval);
            document.removeEventListener('visibilitychange', refetchWhenVisible);
            window.removeEventListener('focus', refetchWhenVisible);
            window.removeEventListener(TOURNAMENT_LOBBY_POLLING_REFRESH_EVENT, refetchWhenVisible);
            abortRef.current?.abort();
            abortRef.current = null;
            sequenceRef.current += 1;
        };
    }, [requestKey, fetchDetails, request]);

    return {
        ...state,
        refetch: fetchDetails,
    };
};

export const useLobbyTournamentSchedule = (args?: { filters?: TournamentScheduleFilters }) => {
    const filters = args?.filters ?? buildTournamentScheduleFilters(getDefaultTournamentScheduleFilterState());

    const query = useQuery({
        queryKey: tournamentScheduleQueryKeys.lobby(filters),
        queryFn: ({ signal }) => requestTournamentScheduleWindowForFilters(filters, signal),
        refetchInterval: TOURNAMENT_SCHEDULE_REFRESH_MS,
    });

    const tournaments = useMemo(
        () => (query.data ?? [])
            .map(normalizeTournamentScheduleItem)
            .filter((tournament): tournament is ITournamentState => tournament !== null)
            .filter((tournament) => matchesTournamentScheduleClientStatusFilter(
                tournament,
                filters.clientStatusFilter
            )),
        [filters.clientStatusFilter, query.data]
    );

    return {
        ...query,
        tournaments,
    };
};

const getHomeTournamentSummaryFilters = (now: Date): TournamentScheduleFilters => {
    const start = new Date(now);
    start.setSeconds(0, 0);

    const end = new Date(start);
    end.setDate(end.getDate() + HOME_TOURNAMENT_SUMMARY_WINDOW_DAYS);

    return {
        startTimeFrom: start.toISOString(),
        startTimeTo: end.toISOString(),
        statusIn: UPCOMING_TOURNAMENT_STATUS_FILTER,
        gameType: 'TEXAS',
    };
};

const mergeHomeTournamentRows = (
    registeredTournaments: ITournamentState[],
    upcomingTournaments: ITournamentState[]
): ITournamentState[] => {
    const seen = new Set<string>();
    const merged: ITournamentState[] = [];

    [...registeredTournaments, ...upcomingTournaments].forEach((tournament) => {
        const id = getTournamentIdentity(tournament);
        if (!id || seen.has(id)) return;

        seen.add(id);
        merged.push(tournament);
    });

    return merged;
};

export const useHomeTournamentSummary = (): Pick<ILobbyTournamentState, 'tournaments' | 'upcomingTournaments' | 'registeredTournaments'> & {
    isLoading: boolean;
    isFetching: boolean;
    error: unknown | null;
} => {
    const scheduleQuery = useQuery({
        queryKey: tournamentScheduleQueryKeys.homeSummary,
        queryFn: async ({ signal }) => {
            const filters = getHomeTournamentSummaryFilters(new Date());
            const page = await requestTournamentSchedulePage({
                ...filters,
                first: HOME_TOURNAMENT_SUMMARY_LIMIT,
            }, signal);

            return Array.isArray(page.items) ? page.items : [];
        },
        refetchInterval: TOURNAMENT_LOBBY_POLL_INTERVAL_MS,
    });

    const scheduledUpcomingTournaments = useMemo(
        () => (scheduleQuery.data ?? [])
            .map(normalizeTournamentScheduleItem)
            .filter((tournament): tournament is ITournamentState => tournament !== null)
            .slice(0, HOME_TOURNAMENT_SUMMARY_LIMIT),
        [scheduleQuery.data]
    );
    const liveRowsRequest = useMemo(
        () => buildTournamentLobbyLiveRowsRequest(scheduledUpcomingTournaments),
        [scheduledUpcomingTournaments]
    );
    const liveRowsQuery = useTournamentLobbyLiveRows(liveRowsRequest);
    const liveRows = useMemo(
        () => getTournamentLobbyLiveRowsForRequest(liveRowsRequest, liveRowsQuery.data),
        [liveRowsRequest, liveRowsQuery.data]
    );
    const upcomingTournaments = useMemo(
        () => mergeScheduleTournamentsWithLobbyLiveRows(scheduledUpcomingTournaments, liveRows),
        [scheduledUpcomingTournaments, liveRows]
    );
    const registeredTournaments = useMemo(
        () => upcomingTournaments.filter((tournament) => tournament.isUserRegistered === true),
        [upcomingTournaments]
    );
    const tournaments = useMemo(
        () => mergeHomeTournamentRows(registeredTournaments, upcomingTournaments),
        [registeredTournaments, upcomingTournaments]
    );

    return {
        tournaments,
        upcomingTournaments,
        registeredTournaments,
        isLoading: scheduleQuery.isLoading || liveRowsQuery.isLoading,
        isFetching: scheduleQuery.isFetching || liveRowsQuery.isFetching,
        error: scheduleQuery.error ?? liveRowsQuery.error ?? null,
    };
};
